import moment from "moment";
import { normalizePaymentEvents } from "../../../lib/reservationInvoiceHelpers";

/** Stable business id used by activity log API (active + past reservations). */
export const getReservationBusinessId = (resDetails, routeReservationId) =>
    resDetails?.id ?? resDetails?.reservationId ?? routeReservationId ?? null;

export const formatReservationDate = (value, format = "MMM D, YYYY") => {
    if (!value) return "";
    return moment(String(value)).format(format);
};

export const formatReservationTime = (value) => {
    if (!value) return "";
    return moment(String(value)).format("h:mm A");
};

export const formatScheduleLabel = (value) => {
    if (!value) return "";
    return moment(String(value)).format("ddd, MMM D · h:mm A");
};

export const formatCreatedMeta = (value) => formatReservationDate(value, "MMM D, YYYY");

export const getReservationProgress = (pickupDate, dropoffDate) => {
    if (!pickupDate || !dropoffDate) return 0;

    const pickup = moment(pickupDate);
    const dropoff = moment(dropoffDate);
    const now = moment();

    if (!pickup.isValid() || !dropoff.isValid() || !dropoff.isAfter(pickup)) {
        return 0;
    }

    if (now.isBefore(pickup)) return 0;
    if (now.isAfter(dropoff)) return 100;

    const totalMs = dropoff.diff(pickup);
    const elapsedMs = now.diff(pickup);

    return Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100));
};

export const getReservationDurationLabel = (pickupDate, dropoffDate, t) => {
    if (!pickupDate || !dropoffDate) return t("notAvailable");

    const pickup = moment(pickupDate);
    const dropoff = moment(dropoffDate);
    const duration = moment.duration(dropoff.diff(pickup));
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();

    if (days > 0 && hours > 0) {
        return t("upcomingDetails.durationDaysHours", { days, hours });
    }
    if (days > 0) {
        return t("upcomingDetails.durationDays", { days });
    }
    if (hours > 0) {
        return t("upcomingDetails.durationHours", { hours });
    }
    return t("upcomingDetails.durationLessThanHour");
};

export const getLocationLabel = (location, fallback = "") => {
    if (!location) return fallback;
    if (typeof location === "string") return location;
    return location.name || location.address || location.label || fallback;
};

export const getVehicleSpecs = (carDetails, resDetails) => {
    const config = carDetails?.config || {};
    const parts = [
        config.year,
        resDetails?.category?.categoryName || resDetails?.carType,
        config.fuelType,
        carDetails?.baseLocation || carDetails?.location?.city,
    ].filter(Boolean);

    return parts.join(" · ");
};

export const getPaymentSummary = (resDetails) => {
    const invoice = resDetails?.invoice ?? {};
    const collected = Number(invoice.deposit ?? 0);
    const outstanding = Number(invoice.due ?? 0);
    const total = Number(invoice.total ?? 0);

    return {
        collected,
        outstanding,
        total,
        hasBalance: outstanding > 0,
    };
};

export const getPaymentRows = (resDetails) => normalizePaymentEvents(resDetails?.invoice ?? {});

export const getReservationStatus = (resDetails, t) => {
    if (!resDetails) {
        return { label: t("notAvailable"), color: "default" };
    }
    if (resDetails.isRejected) {
        return { label: t("rejected"), color: "error" };
    }
    if (resDetails.isCancelled) {
        return { label: t("cancelled"), color: "default" };
    }
    if (!resDetails.flag && resDetails.isCarDroppedOff) {
        return { label: t("completed"), color: "success" };
    }
    if (resDetails.isCarPickedUp && !resDetails.isCarDroppedOff) {
        return { label: t("ongoing"), color: "processing" };
    }
    if (resDetails.isCarSelected && !resDetails.isCarPickedUp) {
        return { label: t("upcomingDetails.awaitingPickup"), color: "warning" };
    }
    if (!resDetails.isApproved) {
        return { label: t("new"), color: "default" };
    }
    if (resDetails.isApproved && !resDetails.isCarLinked) {
        return { label: t("approved"), color: "processing" };
    }
    return { label: t("upcoming"), color: "processing" };
};

export const shouldShowStartBanner = (resDetails) =>
    Boolean(
        resDetails?.flag &&
            resDetails?.isCarSelected &&
            !resDetails?.isCarPickedUp &&
            !resDetails?.isCancelled &&
            !resDetails?.isRejected,
    );

export const shouldShowEndBanner = (resDetails) =>
    Boolean(
        resDetails?.isCarPickedUp &&
            !resDetails?.isCarDroppedOff &&
            !resDetails?.isCancelled &&
            !resDetails?.isRejected,
    );

/** Same eligibility as prod ConfirmReject reject actions (before pickup). */
export const canRejectUpcomingReservation = (resDetails) =>
    Boolean(
        resDetails &&
            resDetails.isRejected === false &&
            resDetails.isCancelled === false &&
            resDetails.isCarPickedUp === false,
    );

export const getPaidAmount = (resDetails) => {
    const invoiceTotal = Number(resDetails?.invoice?.total ?? 0);
    if (invoiceTotal > 0) {
        return invoiceTotal;
    }

    const amount = Number(resDetails?.amount ?? 0);
    const discount = resDetails?.discount?.amount ? Number(resDetails.discount.amount) : 0;
    return amount - discount;
};

export const buildLifecycleSteps = (resDetails, t) => {
    const createdAt = resDetails?.createdAt || resDetails?.pickupDate;
    const paidAmount = getPaidAmount(resDetails);

    return [
        {
            key: "created",
            title: t("upcomingDetails.lifecycle.created"),
            completed: Boolean(resDetails),
            timestamp: createdAt,
        },
        {
            key: "approved",
            title: t("upcomingDetails.lifecycle.approved"),
            completed: Boolean(resDetails?.isApproved),
        },
        {
            key: "linked",
            title: t("upcomingDetails.lifecycle.carLinked"),
            completed: Boolean(resDetails?.isCarLinked),
        },
        {
            key: "selected",
            title: t("upcomingDetails.lifecycle.carSelected"),
            completed: Boolean(resDetails?.isCarSelected),
        },
        {
            key: "pickedUp",
            title: t("upcomingDetails.lifecycle.pickedUp"),
            completed: Boolean(resDetails?.isCarPickedUp),
        },
        {
            key: "droppedOff",
            title: t("upcomingDetails.lifecycle.droppedOff"),
            completed: Boolean(resDetails?.isCarDroppedOff),
        },
        {
            key: "paid",
            title: resDetails?.isPaid
                ? t("upcomingDetails.lifecycle.paidWithAmount", { amount: paidAmount.toFixed(2) })
                : t("upcomingDetails.lifecycle.paidInFull"),
            completed: Boolean(resDetails?.isPaid),
        },
    ];
};

export const getCustomerName = (resDetails, customerDetails) => {
    const firstName = customerDetails?.firstName || resDetails?.firstName || "";
    const lastName = customerDetails?.lastName || resDetails?.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || customerDetails?.email || resDetails?.email || "";
};
